import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 8;
const ZOOM_STEP = 0.25;

function clampZoom(value) {
    return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(value * 100) / 100));
}

function ZoomToolbar({
    zoom,
    pan,
    setZoom,
    resetView,
    onExpand,
    justifyContent = "flex-end",
    sx = {},
    buttonSx = {},
}) {
    return (
        <Stack
            direction="row"
            spacing={0.25}
            alignItems="center"
            justifyContent={justifyContent}
            sx={sx}
        >
            <Tooltip title="Zoom out">
                <span>
                    <IconButton
                        size="small"
                        aria-label="Zoom out"
                        disabled={zoom <= ZOOM_MIN}
                        onClick={() => setZoom((current) => clampZoom(current - ZOOM_STEP))}
                        sx={buttonSx}
                    >
                        <ZoomOutIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>
            <Typography variant="caption" sx={{ minWidth: 40, textAlign: "center", fontWeight: 600 }}>
                {Math.round(zoom * 100)}%
            </Typography>
            <Tooltip title="Zoom in">
                <span>
                    <IconButton
                        size="small"
                        aria-label="Zoom in"
                        disabled={zoom >= ZOOM_MAX}
                        onClick={() => setZoom((current) => clampZoom(current + ZOOM_STEP))}
                        sx={buttonSx}
                    >
                        <ZoomInIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>
            <Tooltip title="Reset zoom and position">
                <span>
                    <IconButton
                        size="small"
                        aria-label="Reset zoom"
                        disabled={zoom === 1 && pan.x === 0 && pan.y === 0}
                        onClick={resetView}
                        sx={buttonSx}
                    >
                        <RestartAltIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>
            {typeof onExpand === "function" ? (
                <Tooltip title="Open fullscreen">
                    <IconButton size="small" aria-label="Open fullscreen" onClick={onExpand} sx={buttonSx}>
                        <OpenInFullIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            ) : null}
        </Stack>
    );
}

/**
 * Zoom (wheel + buttons) and pan (drag) for a single story image.
 */
export default function StoryImageZoomViewport({
    imageUrl,
    minHeight = 360,
    maxHeight = "min(72vh, 800px)",
    fillHeight = false,
    fullWidth = !fillHeight,
    onExpand,
    emptyLabel = "No image",
    toolbarHost = null,
    hideToolbar = false,
    toolbarButtonSx = {},
    sx = {},
}) {
    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [dragging, setDragging] = useState(false);
    const dragRef = useRef(null);
    const viewportRef = useRef(null);
    const wheelHandlerRef = useRef(null);
    const attachedWheelRef = useRef(null);

    useEffect(() => {
        setZoom(1);
        setPan({ x: 0, y: 0 });
        setDragging(false);
        dragRef.current = null;
    }, [imageUrl]);

    wheelHandlerRef.current = (event) => {
        event.preventDefault();
        const direction = event.deltaY < 0 ? 1 : -1;
        setZoom((current) => clampZoom(current + direction * ZOOM_STEP));
    };

    const setViewportNode = useCallback((node) => {
        if (viewportRef.current && attachedWheelRef.current) {
            viewportRef.current.removeEventListener("wheel", attachedWheelRef.current);
        }
        viewportRef.current = node;
        if (node) {
            const handler = (event) => {
                wheelHandlerRef.current?.(event);
            };
            attachedWheelRef.current = handler;
            node.addEventListener("wheel", handler, { passive: false });
        } else {
            attachedWheelRef.current = null;
        }
    }, []);

    const handlePointerDown = (event) => {
        if (event.button !== 0) return;
        event.currentTarget.setPointerCapture(event.pointerId);
        dragRef.current = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            panX: pan.x,
            panY: pan.y,
        };
        setDragging(true);
    };

    const handlePointerMove = (event) => {
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== event.pointerId) return;
        setPan({
            x: drag.panX + (event.clientX - drag.startX),
            y: drag.panY + (event.clientY - drag.startY),
        });
    };

    const endDrag = (event) => {
        if (dragRef.current?.pointerId === event.pointerId) {
            dragRef.current = null;
            setDragging(false);
        }
    };

    const resetView = () => {
        setZoom(1);
        setPan({ x: 0, y: 0 });
    };

    const toolbar = (
        <ZoomToolbar
            zoom={zoom}
            pan={pan}
            setZoom={setZoom}
            resetView={resetView}
            onExpand={onExpand}
            justifyContent={toolbarHost ? "center" : "flex-end"}
            sx={toolbarHost ? undefined : { mb: 1, flexShrink: 0 }}
            buttonSx={toolbarButtonSx}
        />
    );

    return (
        <Box
            sx={{
                width: "100%",
                display: fillHeight ? "flex" : "block",
                flexDirection: fillHeight ? "column" : undefined,
                minHeight: fillHeight ? 0 : undefined,
                ...sx,
            }}
        >
            {toolbarHost
                ? createPortal(toolbar, toolbarHost)
                : hideToolbar
                  ? null
                  : toolbar}

            <Box
                sx={{
                    width: "100%",
                    minHeight: fullWidth ? undefined : fillHeight ? 0 : minHeight,
                    height: fullWidth ? "auto" : fillHeight ? "auto" : maxHeight,
                    flex: fillHeight ? 1 : undefined,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    bgcolor: "action.hover",
                    overflow: "hidden",
                }}
            >
                <Box
                    ref={setViewportNode}
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={endDrag}
                    onPointerCancel={endDrag}
                    onDoubleClick={resetView}
                    sx={{
                        width: "100%",
                        height: fullWidth ? "auto" : "100%",
                        display: fullWidth ? "block" : "flex",
                        alignItems: fullWidth ? undefined : "center",
                        justifyContent: fullWidth ? undefined : "center",
                        overflow: "hidden",
                        cursor: dragging ? "grabbing" : "grab",
                        touchAction: "none",
                        userSelect: "none",
                    }}
                >
                    {imageUrl ? (
                        <Box
                            component="img"
                            src={imageUrl}
                            alt=""
                            draggable={false}
                            sx={
                                fullWidth
                                    ? {
                                          width: "100%",
                                          height: "auto",
                                          display: "block",
                                          verticalAlign: "top",
                                          transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                                          transformOrigin: "center center",
                                          pointerEvents: "none",
                                      }
                                    : {
                                          maxWidth: "100%",
                                          maxHeight: "100%",
                                          objectFit: "contain",
                                          transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                                          transformOrigin: "center center",
                                          pointerEvents: "none",
                                      }
                            }
                        />
                    ) : (
                        <Typography color="text.secondary">{emptyLabel}</Typography>
                    )}
                </Box>
            </Box>
        </Box>
    );
}
